/**
 * Panel DEVELOPER: matriz features × planes + CRUD tokens de plataforma.
 */
import React, { useMemo, useState } from 'react'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormLabel from '@material-ui/core/FormLabel'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import {
  FEATURE_STATUS,
  UDV_PLANS,
  UDV_PLAN_LIST,
  UDV_DEVELOPER_SIM_PLANS,
  UDV_CHROME_THEMES,
  UDV_CHROME_THEME_LIST,
  getEffectiveCatalog,
  getEffectiveTokenRegistry,
  getShowDraft,
  setShowDraft,
  getSimulatedPlan,
  setSimulatedPlan,
  getSimulatedShell,
  setSimulatedShell,
  getSimulatedTheme,
  setSimulatedTheme,
  getPortalSim,
  setPortalSim,
  resetPlanLabOverrides,
  setCatalogOverrides,
  setTokenOverrides,
  PORTAL_CHROME_SECTION_META,
  PORTAL_CHROME_DEFAULT_CONFIG,
  getPortalChromeConfig,
  setPortalChromeOverrides,
  shellShortFromLayout,
} from '../plan'

const PLANS = UDV_PLAN_LIST
const SIM_PLANS = UDV_DEVELOPER_SIM_PLANS
/** Matriz editable incluye portal (patient) para lab. */
const MATRIX_PLANS = UDV_PLAN_LIST.concat([UDV_PLANS.PATIENT])
const SHELLS = ['classic', 'edge', 'studio', 'ohif', 'oviyamng']
const THEMES = UDV_CHROME_THEME_LIST

function newTokenId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
  } catch (e) {
    /* fall through */
  }
  return `tok-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function catalogToOverrideMap(features) {
  const map = {}
  features.forEach((f) => {
    map[f.id] = {
      status: f.status,
      plans: (f.plans || []).slice(),
      label: f.label,
    }
  })
  return map
}

/**
 * @param {{ onChanged?: () => void, skin?: string }} props
 */
function PlanFeaturesPanel({ onChanged }) {
  const [tick, setTick] = useState(0)
  const bump = () => {
    setTick((n) => n + 1)
    if (onChanged) onChanged()
  }

  const catalog = useMemo(() => getEffectiveCatalog(), [tick])
  const tokens = useMemo(() => getEffectiveTokenRegistry(), [tick])
  const simulate = getSimulatedPlan() || UDV_PLANS.SUPREMO
  const simulateShellShort = shellShortFromLayout(getSimulatedShell()) || ''
  const simulateTheme = getSimulatedTheme() || UDV_CHROME_THEMES.DARK
  const showDraft = getShowDraft()
  const portalSim = getPortalSim()
  const portalChrome = useMemo(() => getPortalChromeConfig(), [tick])

  const [newLabel, setNewLabel] = useState('')
  const [newPlan, setNewPlan] = useState(UDV_PLANS.PRO)
  const [newShell, setNewShell] = useState('studio')
  const [exportText, setExportText] = useState('')

  const patchFeature = (id, patch) => {
    const next = catalog.map((f) =>
      f.id === id ? { ...f, ...patch, plans: patch.plans || f.plans } : f
    )
    setCatalogOverrides(catalogToOverrideMap(next))
    bump()
  }

  const togglePlan = (id, plan) => {
    const f = catalog.find((x) => x.id === id)
    if (!f) return
    const has = (f.plans || []).indexOf(plan) >= 0
    const plans = has
      ? (f.plans || []).filter((p) => p !== plan)
      : (f.plans || []).concat([plan])
    patchFeature(id, { plans })
  }

  const saveTokens = (list) => {
    setTokenOverrides(list)
    bump()
  }

  const handleCreateToken = () => {
    const label = String(newLabel || '').trim() || 'Plataforma'
    const token = `udv-${newTokenId().replace(/-/g, '').slice(0, 20)}`
    const row = {
      id: newTokenId(),
      label,
      token,
      plan: newPlan,
      defaultShell: newShell,
      enabled: true,
    }
    saveTokens(tokens.concat([row]))
    setNewLabel('')
  }

  const updateToken = (id, patch) => {
    saveTokens(tokens.map((t) => (t.id === id ? { ...t, ...patch } : t)))
  }

  const removeToken = (id) => {
    saveTokens(tokens.filter((t) => t.id !== id))
  }

  const copyTestUrl = (t) => {
    const shell = t.defaultShell || 'studio'
    // Plantilla para la plataforma: reemplazar {pacs} y {studyId} (o omitirlos).
    const q = new URLSearchParams({
      udvToken: t.token,
      plan: t.plan,
      shell,
      pacs: '{pacs}',
      studyId: '{studyId}',
    })
    const url = `https://udv.tanjousoft.com.ar/?${q.toString()}`
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url)
      }
    } catch (e) {
      /* ignore */
    }
    setExportText(url)
  }

  const handleExport = () => {
    setExportText(JSON.stringify(tokens, null, 2))
  }

  const handleReset = () => {
    resetPlanLabOverrides()
    setExportText('')
    bump()
  }

  const patchPortalChrome = (patch) => {
    const next = {
      hideToolsRail:
        typeof patch.hideToolsRail === 'boolean'
          ? patch.hideToolsRail
          : portalChrome.hideToolsRail,
      sections: {
        ...PORTAL_CHROME_DEFAULT_CONFIG.sections,
        ...(portalChrome.sections || {}),
        ...(patch.sections || {}),
      },
    }
    setPortalChromeOverrides(next)
    bump()
  }

  const resetPortalChrome = () => {
    setPortalChromeOverrides(null)
    bump()
  }

  return (
    <div className="udv-settings__section udv-settings-plan">
      <span className="udv-settings-plan__badge">Developer</span>
      <FormLabel component="legend" className="udv-settings__section-label">
        Planes y features
      </FormLabel>
      <Typography variant="caption" style={{ display: 'block', marginBottom: 12 }}>
        Tokens nuevos rigen en lab hasta exportar y desplegar production. El plan{' '}
        <strong>demo</strong> es showcase limitado (solo 2D básico). Activá{' '}
        <strong>Portal estudios</strong> (o <code>?portal=1</code>) para probar el perfil{' '}
        <code>patient</code> con una URL normal (<code>manifestUrl</code> / <code>pacs</code>).
        Botón URL → plantilla en <code>udv.tanjousoft.com.ar</code> con{' '}
        <code>pacs={'{pacs}'}</code> y <code>studyId={'{studyId}'}</code>.
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={portalSim}
            onChange={(e) => {
              setPortalSim(e.target.checked)
              bump()
            }}
            size="small"
            color="primary"
          />
        }
        label="Portal estudios (perfil patient)"
      />
      {portalSim ? (
        <Typography
          variant="caption"
          style={{ display: 'block', marginBottom: 12, color: '#c9a227' }}
        >
          Activo: perfil <code>patient</code>. Marcá en la columna{' '}
          <strong>patient</strong> qué tools tiene el portal (lab). Query:{' '}
          <code>?portal=1</code> / <code>?portal=0</code>. En chrome se{' '}
          <strong>ocultan</strong> las tools no habilitadas (no solo disabled).
        </Typography>
      ) : null}

      <FormLabel className="udv-settings__section-label" style={{ marginTop: 8 }}>
        Chrome portal (secciones)
      </FormLabel>
      <Typography variant="caption" style={{ display: 'block', marginBottom: 8 }}>
        Por defecto en portal se ocultan Sistema, PET, GSPS, Estudio, Medir, ROI,
        Render, QA y badge Gateway. En teléfono/tablet siguen los iconos ghost y el
        menú joystick. Opcional: ocultar toda la barra Studio (desktop).
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!portalChrome.hideToolsRail}
            onChange={(e) => patchPortalChrome({ hideToolsRail: e.target.checked })}
            size="small"
            color="primary"
          />
        }
        label="Ocultar barra de herramientas Studio (desktop)"
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 8px', marginBottom: 8 }}>
        {PORTAL_CHROME_SECTION_META.map((sec) => (
          <FormControlLabel
            key={sec.id}
            style={{ minWidth: '46%' }}
            control={
              <Checkbox
                checked={portalChrome.sections[sec.id] !== false}
                onChange={(e) =>
                  patchPortalChrome({
                    sections: { [sec.id]: e.target.checked },
                  })
                }
                size="small"
              />
            }
            label={sec.label}
          />
        ))}
      </div>
      <Button size="small" onClick={resetPortalChrome} style={{ marginBottom: 12 }}>
        Restaurar chrome portal por defecto
      </Button>

      <FormLabel className="udv-settings__section-label">Simular plan</FormLabel>
      <RadioGroup
        row
        value={simulate}
        onChange={(e) => {
          const v = e.target.value
          setSimulatedPlan(v === UDV_PLANS.SUPREMO ? null : v)
          bump()
        }}
      >
        {SIM_PLANS.map((p) => (
          <FormControlLabel
            key={p}
            value={p}
            control={<Radio size="small" disabled={portalSim} />}
            label={p === UDV_PLANS.SUPREMO ? 'supremo (root)' : p}
            disabled={portalSim}
          />
        ))}
      </RadioGroup>

      <FormLabel className="udv-settings__section-label" style={{ marginTop: 8 }}>
        Simular shell
      </FormLabel>
      <Typography variant="caption" style={{ display: 'block', marginBottom: 4 }}>
        Lab: fuerza layout (sin <code>?shell=</code>). Vacío = entitlements / default.
      </Typography>
      <RadioGroup
        row
        value={simulateShellShort}
        onChange={(e) => {
          const v = e.target.value
          setSimulatedShell(v || null)
          bump()
        }}
      >
        <FormControlLabel
          value=""
          control={<Radio size="small" />}
          label="(auto)"
        />
        {SHELLS.map((s) => (
          <FormControlLabel
            key={s}
            value={s}
            control={<Radio size="small" />}
            label={s}
          />
        ))}
      </RadioGroup>

      <FormLabel className="udv-settings__section-label" style={{ marginTop: 8 }}>
        Simular theme (OHIF / OviyamNG)
      </FormLabel>
      <Typography variant="caption" style={{ display: 'block', marginBottom: 4 }}>
        Paleta chrome OHIF/OviyamNG: <code>?theme=dark|brand|light</code>. OviyamNG: dark=Mayam, brand=burdeos/amber, light≈jQuery UI. Classic/Studio/Edge lo ignoran.
      </Typography>
      <RadioGroup
        row
        value={simulateTheme}
        onChange={(e) => {
          setSimulatedTheme(e.target.value)
          bump()
        }}
      >
        {THEMES.map((t) => (
          <FormControlLabel
            key={t}
            value={t}
            control={<Radio size="small" />}
            label={t}
          />
        ))}
      </RadioGroup>

      <FormControlLabel
        control={
          <Checkbox
            checked={showDraft}
            onChange={(e) => {
              setShowDraft(e.target.checked)
              bump()
            }}
            size="small"
          />
        }
        label="Mostrar features draft"
      />

      <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
        Features (columnas comerciales + <code>patient</code> portal)
      </Typography>
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Feature</th>
              <th>Draft</th>
              {MATRIX_PLANS.map((p) => (
                <th
                  key={p}
                  style={
                    p === UDV_PLANS.PATIENT ? { color: '#c9a227' } : undefined
                  }
                >
                  {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.map((f) => (
              <tr key={f.id}>
                <td>{f.label}</td>
                <td style={{ textAlign: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={f.status === FEATURE_STATUS.DRAFT}
                    onChange={(e) =>
                      patchFeature(f.id, {
                        status: e.target.checked
                          ? FEATURE_STATUS.DRAFT
                          : FEATURE_STATUS.PUBLISHED,
                      })
                    }
                  />
                </td>
                {MATRIX_PLANS.map((p) => (
                  <td key={p} style={{ textAlign: 'center' }}>
                    <Checkbox
                      size="small"
                      checked={(f.plans || []).indexOf(p) >= 0}
                      onChange={() => togglePlan(f.id, p)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Typography variant="subtitle2" style={{ marginTop: 24, marginBottom: 8 }}>
        Tokens de plataforma
      </Typography>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <TextField
          size="small"
          label="Label"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          variant="outlined"
        />
        <TextField
          select
          size="small"
          label="Plan máx."
          value={newPlan}
          onChange={(e) => setNewPlan(e.target.value)}
          variant="outlined"
          SelectProps={{ native: true }}
          style={{ minWidth: 100 }}
        >
          {PLANS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Shell"
          value={newShell}
          onChange={(e) => setNewShell(e.target.value)}
          variant="outlined"
          SelectProps={{ native: true }}
          style={{ minWidth: 100 }}
        >
          {SHELLS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </TextField>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleCreateToken}
        >
          Crear token
        </Button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Label</th>
              <th style={{ textAlign: 'left' }}>Token</th>
              <th>Plan</th>
              <th>Shell</th>
              <th>On</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tokens.map((t) => (
              <tr key={t.id}>
                <td>{t.label}</td>
                <td
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 11,
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={t.token}
                >
                  {t.token}
                </td>
                <td>
                  <select
                    value={t.plan}
                    onChange={(e) => updateToken(t.id, { plan: e.target.value })}
                  >
                    {PLANS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={t.defaultShell || 'studio'}
                    onChange={(e) =>
                      updateToken(t.id, { defaultShell: e.target.value })
                    }
                  >
                    {SHELLS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={t.enabled !== false}
                    onChange={(e) =>
                      updateToken(t.id, { enabled: e.target.checked })
                    }
                  />
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => copyTestUrl(t)}>
                    URL
                  </Button>
                  <Button size="small" onClick={() => removeToken(t.id)}>
                    Quitar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={handleExport}>
          Exportar tokens JSON
        </Button>
        <Button size="small" variant="outlined" onClick={handleReset}>
          Restablecer defaults
        </Button>
      </div>
      {exportText ? (
        <TextField
          multiline
          fullWidth
          rows={6}
          value={exportText}
          style={{ marginTop: 12 }}
          variant="outlined"
          inputProps={{ style: { fontFamily: 'monospace', fontSize: 11 } }}
        />
      ) : null}
    </div>
  )
}

export default PlanFeaturesPanel
